import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Typography,
  Box,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const trimmed = cookie.trim();

    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return null;
}

const getAccessToken = () => {
  return (
    localStorage.getItem("access") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();

    if (text) {
      return text;
    }
  }

  return "";
};

const buildCreatedPerson = (raw, fallback) => {
  const base = raw?.persona || raw?.cliente || raw?.data || raw || {};

  const identificacion = firstNonEmpty(
    base.identificacion,
    base.ruc,
    base.cedula,
    base.identificacion_cliente,
    fallback.ruc,
    fallback.cedula
  );

  return {
    id: firstNonEmpty(base.id, identificacion, fallback.razon_social),
    identificacion,
    razon_social: firstNonEmpty(
      base.razon_social,
      base.nombre,
      fallback.razon_social
    ),
    correo: firstNonEmpty(base.correo, base.email, fallback.correo),
    telefono: firstNonEmpty(base.telefono, fallback.telefono),
    direccion: firstNonEmpty(base.direccion, fallback.direccion),
    ruc: firstNonEmpty(base.ruc, fallback.ruc),
    cedula: firstNonEmpty(base.cedula, fallback.cedula),
    es_cliente:
      typeof base.es_cliente === "boolean"
        ? base.es_cliente
        : fallback.es_cliente,
    es_proveedor:
      typeof base.es_proveedor === "boolean"
        ? base.es_proveedor
        : fallback.es_proveedor,
    es_empleado: false,
    es_extranjero: false,
    tipo: firstNonEmpty(base.tipo, fallback.tipo),
    nombre_comercial: firstNonEmpty(
      base.nombre_comercial,
      fallback.nombre_comercial
    ),
  };
};

const getInitialForm = () => ({
  razon_social: "",
  nombre_comercial: "",
  cedula: "",
  ruc: "",
  telefono: "",
  correo: "",
  direccion: "",
});

export default function AddPerson({
  open,
  onClose,
  tipoDocumento,
  onCreated,
}) {
  const isProviderMode = useMemo(
    () => String(tipoDocumento || "").toUpperCase() === "PROVEEDOR",
    [tipoDocumento]
  );

  const [tipo, setTipo] = useState("N");
  const [esCliente, setEsCliente] = useState(!isProviderMode);
  const [esProveedor, setEsProveedor] = useState(isProviderMode);
  const [saving, setSaving] = useState(false);
  const [consultandoSri, setConsultandoSri] = useState(false);
  const [form, setForm] = useState(getInitialForm());
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "error",
    message: "",
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setTipo("N");
    setEsCliente(!isProviderMode);
    setEsProveedor(isProviderMode);
    setConsultandoSri(false);
    setForm(getInitialForm());
    setErrors({});
  }, [open, isProviderMode]);

  const showSnackbar = (type, message) => {
    setSnackbar({
      open: true,
      type,
      message,
    });
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const consultarSri = async () => {
    const identificacion = form.cedula.trim();

    if (!/^\d{10}$/.test(identificacion)) {
      showSnackbar(
        "warning",
        "Ingrese una cédula válida de 10 dígitos."
      );
      return;
    }

    const token = getAccessToken();

    if (!token) {
      showSnackbar(
        "error",
        "No se encontró el token de autenticación."
      );
      return;
    }

    setConsultandoSri(true);

    try {
      const response = await fetch("/clientes/consultar-sri/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          identificacion,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.encontrado) {
        showSnackbar(
          response.status === 404 ? "warning" : "error",
          data?.mensaje ||
            data?.error ||
            "No fue posible consultar los datos en el SRI."
        );
        return;
      }

      const persona = data.persona || {};

      setTipo(persona.tipo || "N");

      setForm((prev) => ({
        ...prev,
        cedula: persona.cedula || prev.cedula,
        ruc: persona.ruc || prev.ruc,
        razon_social:
          persona.razon_social ||
          prev.razon_social,
        nombre_comercial:
          persona.nombre_comercial ||
          persona.razon_social ||
          prev.nombre_comercial,
      }));

      setErrors((prev) => ({
        ...prev,
        razon_social: "",
      }));

      showSnackbar(
        "success",
        "Datos encontrados en el SRI."
      );
    } catch {
      showSnackbar(
        "error",
        "No se pudo conectar con el servidor."
      );
    } finally {
      setConsultandoSri(false);
    }
  };

  const validate = () => {
    const next = {};

    if (!String(form.razon_social || "").trim()) {
      next.razon_social =
        tipo === "N"
          ? "Ingresa el nombre completo"
          : "Ingresa la razón social";
    }

    if (
      form.correo &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        String(form.correo).trim()
      )
    ) {
      next.correo = "Correo inválido";
    }

    if (!esCliente && !esProveedor) {
      next.roles = "Selecciona cliente o proveedor";
    }

    setErrors(next);

    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      tipo,
      razon_social: String(
        form.razon_social || ""
      ).toUpperCase().trim(),
      nombre_comercial: String(
        form.nombre_comercial || ""
      ).toUpperCase().trim(),
      cedula: String(form.cedula || "").trim(),
      ruc: String(form.ruc || "").trim(),
      telefono: String(form.telefono || "").trim(),
      correo: String(form.correo || "").trim(),
      direccion: String(form.direccion || "").trim(),
      es_cliente: esCliente,
      es_proveedor: esProveedor,
      es_empleado: false,
      es_extranjero: false,
    };

    setSaving(true);

    try {
      const response = await fetch("/clientes/crear/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const contentType = (
        response.headers.get("content-type") || ""
      ).toLowerCase();

      const data = contentType.includes("application/json")
        ? await response.json().catch(() => ({}))
        : {};

      if (!response.ok) {
        let message = "Error al guardar la persona.";

        if (typeof data?.error === "string") {
          message = data.error;
        } else if (
          data?.error &&
          typeof data.error === "object"
        ) {
          const firstValue = Object.values(data.error)[0];

          message = Array.isArray(firstValue)
            ? firstValue[0]
            : String(firstValue);
        } else if (data?.message) {
          message = data.message;
        }

        showSnackbar("error", message);
        return;
      }

      const created = buildCreatedPerson(data, payload);

      onCreated?.(created);
      onClose?.();

      showSnackbar(
        "success",
        `${
          isProviderMode
            ? "Proveedor"
            : "Cliente"
        } creado correctamente.`
      );
    } catch {
      showSnackbar(
        "error",
        "No se pudo conectar con el servidor."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={
          saving || consultandoSri
            ? undefined
            : onClose
        }
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.25,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            bgcolor: "#f7faff",
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "#1976d2",
              color: "#fff",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.22)",
            }}
          >
            {isProviderMode
              ? <BusinessIcon />
              : <PersonAddAlt1Icon />}
          </Box>

          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {isProviderMode
                ? "Nuevo proveedor"
                : "Nuevo cliente"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#5f6b7a",
                mt: 0.25,
              }}
            >
              Crea la persona sin salir del documento
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <Box
          component="form"
          onSubmit={handleSubmit}
        >
          <DialogContent
            sx={{
              px: 3,
              py: 3,
            }}
          >
            <Grid container spacing={2.2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    Tipo de persona
                  </InputLabel>

                  <Select
                    value={tipo}
                    label="Tipo de persona"
                    onChange={(event) =>
                      setTipo(event.target.value)
                    }
                  >
                    <MenuItem value="N">
                      Natural
                    </MenuItem>

                    <MenuItem value="J">
                      Jurídica
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    minHeight: 56,
                    px: 1.5,
                    border: "1px solid #d9e1ea",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    backgroundColor: "#fbfdff",
                  }}
                >
                  <FormGroup
                    row
                    sx={{
                      gap: 0.5,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={esCliente}
                          onChange={(event) =>
                            setEsCliente(
                              event.target.checked
                            )
                          }
                          disabled={isProviderMode}
                        />
                      }
                      label="Cliente"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={esProveedor}
                          onChange={(event) =>
                            setEsProveedor(
                              event.target.checked
                            )
                          }
                          disabled={isProviderMode}
                        />
                      }
                      label="Proveedor"
                    />
                  </FormGroup>
                </Box>

                {!!errors.roles && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#d32f2f",
                      mt: 0.75,
                      display: "block",
                    }}
                  >
                    {errors.roles}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Cédula"
                  value={form.cedula}
                  onChange={(event) => {
                    const value =
                      event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);

                    setForm((prev) => ({
                      ...prev,
                      cedula: value,
                    }));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      consultarSri();
                    }
                  }}
                  fullWidth
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 10,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={consultarSri}
                          disabled={consultandoSri}
                          edge="end"
                          title="Consultar en el SRI"
                        >
                          {consultandoSri
                            ? (
                              <CircularProgress
                                size={20}
                              />
                            )
                            : <SearchIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="RUC"
                  value={form.ruc}
                  onChange={(event) => {
                    const value =
                      event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 13);

                    setForm((prev) => ({
                      ...prev,
                      ruc: value,
                    }));
                  }}
                  fullWidth
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 13,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={
                    tipo === "N"
                      ? "Nombre completo"
                      : "Razón social"
                  }
                  value={form.razon_social}
                  onChange={handleFieldChange(
                    "razon_social"
                  )}
                  fullWidth
                  required
                  error={!!errors.razon_social}
                  helperText={
                    errors.razon_social || " "
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Nombre comercial"
                  value={form.nombre_comercial}
                  onChange={handleFieldChange(
                    "nombre_comercial"
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Teléfono"
                  value={form.telefono}
                  onChange={handleFieldChange(
                    "telefono"
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Correo electrónico"
                  type="email"
                  value={form.correo}
                  onChange={handleFieldChange(
                    "correo"
                  )}
                  fullWidth
                  error={!!errors.correo}
                  helperText={errors.correo || " "}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  value={form.direccion}
                  onChange={handleFieldChange(
                    "direccion"
                  )}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2.25,
              borderTop: "1px solid #eef2f6",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
              }}
            >
              {isProviderMode
                ? "Se asociará como proveedor"
                : "Se asociará como cliente"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 1.25,
              }}
            >
              <Button
                onClick={onClose}
                disabled={
                  saving || consultandoSri
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                }}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={
                  saving || consultandoSri
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.25,
                  bgcolor: "#1565C0",
                  "&:hover": {
                    bgcolor: "#0D47A1",
                  },
                }}
              >
                {saving
                  ? "Guardando..."
                  : "Guardar y seleccionar"}
              </Button>
            </Box>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity={snackbar.type}
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}